import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import InlineEditableInput from "@/components/InlineEditableInput";
import { Trash2Icon } from "lucide-react";
import { SectionHeader } from "./inspector-helpers";
import { DeleteConfirmDialog } from "@/components/delete-confirm-dialog";
import { CodeGenerateButton } from "@/components/code-generate-button";
import { toCodeIdentifier } from "@/lib/utils";
import type { BaseTemplateAttribute } from "./types";

interface InspectorTabularProps {
  attribute: BaseTemplateAttribute;
  isLocked: boolean;
  onUpdateAttributeType: (id: number, updates: Record<string, unknown>) => void;
  onRemove: (id: number) => void;
}

export function InspectorTabular({
  attribute,
  isLocked,
  onUpdateAttributeType,
  onRemove,
}: InspectorTabularProps) {
  const handleTypeUpdate = (updates: Record<string, unknown>) =>
    onUpdateAttributeType(attribute.id, updates);

  return (
    <div className="space-y-0">
      {/* Identity */}
      <SectionHeader title="Table Settings" />
      <div className="px-3 py-3 space-y-3">
        <div>
          <Label className="text-xs font-medium">Name</Label>
          <InlineEditableInput
            autoFocus={attribute.name === ""}
            value={attribute.name || ""}
            onSave={(next) => handleTypeUpdate({ name: next })}
            placeholder="Table name"
            disabled={isLocked}
            inputClassName="h-7 text-sm mt-1"
          />
        </div>
        <div>
          <Label className="text-xs font-medium">Description</Label>
          <InlineEditableInput
            value={attribute.description || ""}
            onSave={(next) => handleTypeUpdate({ description: next })}
            placeholder="Click to edit"
            disabled={isLocked}
            inputClassName="h-7 mt-1"
          />
        </div>
        <div>
          <Label className="text-xs font-medium">Code</Label>
          <div className="flex items-start gap-1 mt-1">
            <InlineEditableInput
              className="flex-1"
              value={attribute.code || ""}
              onSave={(next) =>
                handleTypeUpdate({ code: toCodeIdentifier(next) || null })
              }
              placeholder="e.g. measurements"
              disabled={isLocked}
              inputClassName="h-7 text-sm font-mono"
            />
            <CodeGenerateButton
              sourceName={attribute.name ?? ""}
              onGenerate={(code) => handleTypeUpdate({ code })}
              disabled={isLocked}
            />
          </div>
        </div>
      </div>

      {/* Delete */}
      {!isLocked && (
        <div className="px-3 py-3 border-t border-border">
          <DeleteConfirmDialog
            title="Delete Table"
            description={`Are you sure you want to delete "${attribute.name || "Untitled"}"? This will remove the table and all its columns. This cannot be undone.`}
            onConfirm={() => onRemove(attribute.id)}
            trigger={
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10 text-xs"
              >
                <Trash2Icon className="h-3 w-3 mr-1" />
                Delete table
              </Button>
            }
          />
        </div>
      )}
    </div>
  );
}
